import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GlassInput } from '../components/ui/glass-input';
import { CopperButton } from '../components/ui/copper-button';
import { PersonaIcon } from '../components/ui/persona-icon';
import { LogIn } from 'lucide-react';

const demoCredentials = [
  { email: 'patient@fora.com', password: 'patient123', role: 'patient' },
  { email: 'receptionist@fora.com', password: 'receptionist123', role: 'receptionist' },
  { email: 'doctor@fora.com', password: 'doctor123', role: 'doctor' },
  { email: 'billing@fora.com', password: 'billing123', role: 'billing' },
  { email: 'owner@fora.com', password: 'owner123', role: 'owner' }
];

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/personas');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleQuickLogin = async (cred) => {
    setEmail(cred.email);
    setPassword(cred.password);
    setError('');
    setLoading(true);

    const result = await login(cred.email, cred.password);
    
    if (result.success) {
      navigate('/personas');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo/Brand */}
        <div className="text-center mb-12">
          <h1 
            className="text-heading-1 mb-3"
            style={{ color: 'var(--color-text-primary)' }}
          >
            FORA
          </h1>
          <p 
            className="text-body-sm"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            Premium service business operating system
          </p>
        </div>

        {/* Login Form */}
        <div className="glass-primary rounded-[var(--radius-2xl)] p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <GlassInput
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              data-testid="login-email-input"
            />
            
            <GlassInput
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              error={error}
              data-testid="login-password-input"
            />

            <CopperButton
              type="submit"
              size="lg"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              <LogIn size={18} strokeWidth={1.75} />
              {loading ? 'Signing In...' : 'Sign In'}
            </CopperButton>
          </form>
        </div>

        {/* Demo Credentials */}
        <div className="glass-subtle rounded-[var(--radius-xl)] p-6">
          <p 
            className="text-overline mb-4 text-center"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Quick Demo Access
          </p>
          <div className="grid grid-cols-5 gap-3">
            {demoCredentials.map((cred) => (
              <button
                key={cred.role}
                onClick={() => handleQuickLogin(cred)}
                className="flex flex-col items-center gap-2 p-3 rounded-[var(--radius-md)] hover:bg-[rgba(61,90,128,0.08)] transition-all duration-180 interactive"
                disabled={loading}
                data-testid={`quick-login-${cred.role}`}
              >
                <PersonaIcon persona={cred.role} size="sm" />
                <span 
                  className="text-xs font-semibold font-['Sora'] capitalize"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {cred.role}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;